import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Eyebrow, HighlightedText, ArrowLink, VisuallyHidden } from "blog/components/ui.js";
import { ReactComponent as ExternalLinkIcon } from "feather-icons/dist/icons/external-link.svg";
import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";
import { ReactComponent as SvgDecoratorBlob3 } from "images/svg-decorator-blob-3.svg";
import { acteurs as acteursParDefaut, autres as autresParDefaut } from "home/data/acteurs.js";

/**
 * Section « Cartographie » — les acteurs clés de l’écosystème tech et innovation du bassin de Besançon.
 * Six cartes (icône feather + surtitre violet, sans logo ni image tierce) vers les sites officiels,
 * un cadrage factuel intégré à la description, puis une ligne « Et aussi » qui cite les autres acteurs
 * autorisés et, sur une ligne à part, propose de suggérer une structure absente (lien vers /contact).
 *
 * Aucune prop n’est obligatoire : les valeurs par défaut correspondent au texte validé.
 * Les données (cartes + ligne « Et aussi ») vivent dans src/home/data/acteurs.js.
 * Primitives de la DA importées de blog/components/ui.js (lecture seule).
 *
 * Accessibilité : chaque carte est un lien ; son nom accessible est limité à « Nom + Site officiel
 * (s’ouvre dans un nouvel onglet) » via aria-labelledby, le h3 restant visible dans la carte.
 */

const NEW_TAB = " (s’ouvre dans un nouvel onglet)";

/* En-tête */
const HeadingContainer = tw.div`flex flex-col items-center text-center`;
const Subheading = tw(Eyebrow)`block mb-3`;
const Heading = tw(SectionHeading)``;
/* secondary-300 : même bleu nuit que la DA, mais contraste AA sur fond blanc. */
const Description = tw(SectionDescription)`mx-auto text-center text-secondary-300`;

/* Grille et cartes (même gap-8 que la grille de « Derniers articles »). */
const Grid = tw.div`mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8`;
const Card = styled.a`
  ${tw`flex flex-col h-full bg-gray-100 rounded-lg p-6 border-2 border-gray-200 hocus:border-primary-500 hocus:shadow-raised transition duration-300 transform hocus:-translate-y-1 focus:outline-none focus:shadow-outline`}
`;
/* Surtitre violet avec icône, comme le motif « uppercase tracking-widest text-xs » de la DA. */
const Kind = tw.span`inline-flex items-center text-xs uppercase tracking-widest font-bold text-primary-500`;
const KindIcon = styled.span`
  ${tw`mr-2 flex-shrink-0`}
  svg {
    ${tw`w-4 h-4`}
  }
`;
const Name = tw.h3`mt-4 text-lg font-bold text-secondary-500 group-hocus:text-primary-500 transition duration-300`;
/* Même gris que le texte des PostCards affichées juste au-dessus. */
const Text = tw.p`mt-3 text-sm text-gray-600 font-medium leading-relaxed flex-grow`;
const Footer = tw.span`mt-4 text-sm font-bold text-primary-500 inline-flex items-center`;
const FooterIcon = tw(ExternalLinkIcon)`w-4 h-4 ml-2 flex-shrink-0`;

/* Sous la grille : la ligne « Et aussi … », puis la suggestion sur son propre paragraphe. */
const Also = tw.div`mt-10 text-center text-sm font-medium text-gray-700 leading-loose max-w-2xl mx-auto`;
const AlsoLine = tw.p``;
const SuggestLine = tw.p`mt-2`;
/* Liens soulignés au repos (gris discret) pour se distinguer du texte courant. */
const AlsoLink = tw.a`font-bold text-secondary-500 border-b-2 border-gray-400 hocus:text-primary-500 hocus:border-primary-500 transition duration-300 rounded focus:outline-none focus:shadow-outline`;
const Separator = tw.span`text-gray-500`;
const SuggestLink = tw(ArrowLink)`ml-1 rounded focus:shadow-outline`;

/* Décor : un seul blob rose en bas à droite, comme ThreeColSimple.js, avec hauteur fixée
   (sans h-64, la boîte du SVG ferait 256×600 px et déborderait sur la section suivante). */
const DecoratorBlob = tw(
  SvgDecoratorBlob3
)`pointer-events-none absolute right-0 bottom-0 w-64 h-64 opacity-25 transform translate-x-32 translate-y-40 -z-10`;

export default function ActeursCles({
  subheading = "Cartographie",
  heading = (
    <>
      Les acteurs clés de <HighlightedText>l’écosystème.</HighlightedText>
    </>
  ),
  description = "Institut de recherche, université, école d’ingénieurs, technopole, salon, hôpital : les structures qui font l’innovation dans le Grand Besançon. Une sélection de départ, sans classement, enrichie au fil des articles.",
  acteurs = acteursParDefaut,
  autres = autresParDefaut,
  footerText = "Site officiel",
  alsoLabel = "Et aussi :",
  suggestText = "Vous représentez une structure absente de cette liste ?",
  ctaText = "Suggérez-la",
  ctaUrl = "/contact",
}) {
  return (
    <Container id="ecosysteme">
      <ContentWithPaddingXl>
        <HeadingContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description && <Description>{description}</Description>}
        </HeadingContainer>

        <Grid>
          {acteurs.map((acteur, index) => (
            <Card
              key={acteur.url}
              className="group"
              href={acteur.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-labelledby={`acteur-${index}-name acteur-${index}-cta`}
            >
              <Kind>
                {acteur.icon && (
                  <KindIcon>
                    <acteur.icon aria-hidden="true" focusable="false" />
                  </KindIcon>
                )}
                {acteur.kind}
              </Kind>
              <Name id={`acteur-${index}-name`}>{acteur.name}</Name>
              <Text>{acteur.text}</Text>
              <Footer id={`acteur-${index}-cta`}>
                {footerText}
                <VisuallyHidden>{NEW_TAB}</VisuallyHidden>
                <FooterIcon aria-hidden="true" focusable="false" />
              </Footer>
            </Card>
          ))}
        </Grid>

        <Also>
          {autres.length > 0 && (
            <AlsoLine>
              {alsoLabel}{" "}
              {autres.map((autre, i) => (
                <React.Fragment key={autre.url}>
                  {i > 0 && <Separator aria-hidden="true">{" · "}</Separator>}
                  <AlsoLink href={autre.url} target="_blank" rel="noopener noreferrer">
                    {autre.name}
                    <VisuallyHidden>{NEW_TAB}</VisuallyHidden>
                  </AlsoLink>
                </React.Fragment>
              ))}
            </AlsoLine>
          )}
          {suggestText && ctaText && (
            <SuggestLine>
              {suggestText}
              <SuggestLink to={ctaUrl}>
                {ctaText}
                <ArrowRightIcon aria-hidden="true" focusable="false" />
              </SuggestLink>
            </SuggestLine>
          )}
        </Also>
      </ContentWithPaddingXl>
      <DecoratorBlob aria-hidden="true" focusable="false" />
    </Container>
  );
}
